/* The phase strip, transcribed from `Builder planu - tydzien - programator.dc.html`'s second row.
 *
 * The mock's own declaration:
 *
 *   flex:none; display:flex; align-items:center; gap:6px; height:46px; padding:0 20px;
 *   background:var(--bg-card); border-bottom:1px solid var(--border)
 *
 * ── The scroll fade is GONE ──────────────────────────────────────────────────────────────────
 * This band used to wrap the tablist in a `.strip` carrying a gradient over its right edge, so a
 * plan with more phases than the band could show said so. The mock draws no fade, and nothing the
 * mock does not draw may be visible (owner, 8 September 2026). The band still scrolls, the selected
 * tab is still scrolled into view, and `useOverflowEdge` is no longer read here.
 *
 * ── `--text-3` is the range's and the tag's colour, because it is the mock's ─────────────────
 * The note that used to open this file — KANON's tertiary ink fails AA, so the hierarchy is carried
 * by size and weight instead — is withdrawn. It produced a screen the owner rejected beside the
 * mock. The shortfall stands and is flagged: the range and the tag are 11px and 9.5px in an ink
 * that measures under AA on `--bg-card` in both themes. Nothing is told apart by it — the range is
 * a duplicate of the band total and the tag repeats the phase's own type — but it is a real
 * reading cost and it is the designer's to answer. */

.bar {
  display: flex;
  flex: none;
  gap: var(--space-6px);
  align-items: center;
  height: 46px;
  padding: var(--space-0) var(--space-20px);
  background: var(--bg-card);
  border-bottom: var(--border-hairline) solid var(--border);
}

/* The tablist itself. It scrolls rather than wrapping — the bar is one 46px row — and it takes the
 * same 6px gap the bar declares, so a plan of three phases and a plan of nine draw the same rhythm.
 * `scrollbar-width: none` is the ONE thing here the mock does not state and this needs: the mock's
 * three chips never overflow, and a native horizontal scrollbar inside a 46px row would eat 10px of
 * it and draw a grey slab the mock has no picture of. */
.tabs {
  display: flex;
  gap: var(--space-6px);
  align-items: center;
  min-width: 0;
  overflow: auto hidden;
  scrollbar-width: none;
}

.tabs::-webkit-scrollbar {
  display: none;
}

/* The mock's `phSt`: radius 10, `6px 12px 6px 15px` — the extra 3px on the left is the room the
 * accent bar stands in — a transparent ground under a `--border` hairline, and `align-items:
 * baseline` so the 12px name, the 11px range and the 9.5px tag sit on one line. */
.tab {
  position: relative;
  display: inline-flex;
  flex: none;
  gap: var(--space-2);
  align-items: baseline;
  padding: var(--space-6px) var(--space-3) var(--space-6px) var(--space-15px);
  overflow: hidden;
  font-family: var(--font-body);

  /* The mock's chip is a `<span>`, which inherits the screen's 16px. A `<button>` would drop to the
   * user agent's 13.33px and change the baseline every child sits on. */
  font-size: inherit;
  cursor: pointer;
  background: transparent;
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-sm);
  transition-property: background, border-color;
  transition-duration: var(--dur-base);
  transition-timing-function: var(--ease-default);
}

.tabActive {
  background: var(--bg-chip);
  border-color: var(--border-2);
}

.tab:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-2px);
}

/* The 3px bar down the chip's left edge, painted only on the phase the canvas is showing. The mock
 * keeps the element on every chip and switches its background, so the chip's own 15px left padding
 * never changes and nothing shifts as the selection moves. */
.pip {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 3px;
  background: transparent;
  border-radius: var(--radius-bar) 0 0 var(--radius-bar);
  transition-property: background;
  transition-duration: var(--dur-base);
  transition-timing-function: var(--ease-default);
}

/* The mock's `PH[].c`: the first phase in the accent, an ordinary phase in `--text-2`, a deload in
 * the structural blue. Only the SELECTED chip paints — see `.pip`. */
.tabActive .pipAccent {
  background: var(--accent);
}

.tabActive .pipNeutral {
  background: var(--text-2);
}

.tabActive .pipDeload {
  background: var(--warm-blue);
}

.name {
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  white-space: nowrap;
}

.tabActive .name {
  font-weight: var(--fw-extrabold);
  color: var(--text);
}

.range {
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  color: var(--text-3);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.tag {
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-tag);
  text-transform: uppercase;
  color: var(--text-3);
}

/* The add-phase trigger, and it is a MARK rather than a box (owner, 17 September 2026).
 *
 * The canon draws a dashed ghost with "+ Faza" inside it. The owner cut the word first, and then
 * looked at what was left — a 26px dashed square holding one glyph — and asked for either something
 * with real depth or nothing at all. Nothing at all is the one that belongs here: this app has no
 * depth vocabulary to draw on (no coloured glow, flat fills, neutral shadows only), so a "3D" plus
 * would be the first of its kind on any surface and would then have to be explained everywhere else.
 *
 * So the box goes and the sign stays, drawn exactly like the top bar's ⋯ two rows above it: quiet
 * ink at rest, the chip ground and full ink under a pointer or while the menu is open. One icon
 * button, one treatment, and the two controls on this screen that open a menu now look like each
 * other. The hit area is unchanged at 26x26 — what was removed is the border, not the target.
 *
 * `--text-2` and not `--text-3` for the reason the previous revision recorded: with the word gone
 * the glyph IS the control, so it owes text contrast. 6.63:1 in Flame, 8.96:1 in Flame Dark. */
.addPhase {
  display: inline-grid;
  flex: none;
  place-items: center;
  width: 26px;
  height: 26px;
  padding: var(--space-0);
  font-family: var(--font-body);
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: var(--radius-8px);
}

.addPhase:hover,
.addPhase[data-popup-open] {
  color: var(--text);
  background: var(--bg-chip);
}

.addPhase:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-2px);
}

.spacer {
  flex: 1;
}

/* "8 tygodni · 3 fazy", at the bar's right edge. */
.total {
  flex: none;
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  color: var(--text-3);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
